import { LessonCart } from '../types/booking';

const CART_KEY = 'cart';
const CART_TIMESTAMP_KEY = 'cart_updated_at';

/** Maximum age of the cart in milliseconds (7 days). */
const CART_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Read the cart from localStorage.  Returns an empty array when the
 * stored cart is missing, unparseable, or older than {@link CART_TTL_MS}.
 */
function isEmptyCartJson(json: string): boolean {
    const trimmed = json.trim();
    return trimmed === '' || trimmed === '[]';
}

export function readCart(): LessonCart[] {
    const json = localStorage?.getItem(CART_KEY);
    if (!json || isEmptyCartJson(json)) return [];

    const timestamp = Number(localStorage?.getItem(CART_TIMESTAMP_KEY) || '0');
    if (timestamp > 0 && Date.now() - timestamp > CART_TTL_MS) {
        clearCart();
        return [];
    }

    try {
        const cart = JSON.parse(json) as LessonCart[];
        if (!Array.isArray(cart) || cart.length === 0) {
            return [];
        }
        // Carts saved before cart_updated_at existed: start the 7-day TTL from first read.
        if (timestamp <= 0) {
            writeCart(cart);
        }
        return cart;
    } catch {
        clearCart();
        return [];
    }
}

/**
 * Persist the cart to localStorage and refresh the timestamp.
 */
export function writeCart(cart: LessonCart[]): void {
    localStorage?.setItem(CART_KEY, JSON.stringify(cart));
    localStorage?.setItem(CART_TIMESTAMP_KEY, String(Date.now()));
}

/**
 * Empty the cart and remove the timestamp.
 */
export function clearCart(): void {
    localStorage?.setItem(CART_KEY, '[]');
    localStorage?.removeItem(CART_TIMESTAMP_KEY);
}
